/**
 * Notification Bell (navbar)
 * - Polls /notifications/unread-count every 30s
 * - Opens a dropdown panel with the latest notifications
 * - Plays a subtle sound when a NEW notification arrives (if permission granted)
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, BellRing, Check, Trash2, Inbox } from "lucide-react";
import { useAuth, API } from "@/context/AppContext";

const POLL_INTERVAL_MS = 30000;
const MAX_PREVIEW = 8;

const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - then) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const previousUnreadRef = useRef(0);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      const newCount = res.data.unread || 0;
      // Subtle sound + browser notification when count goes UP
      if (newCount > previousUnreadRef.current && previousUnreadRef.current > 0) {
        try {
          // Tiny in-page chime via Web Audio API (no asset needed)
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.value = 880;
          g.gain.setValueAtTime(0.05, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
          o.start(); o.stop(ctx.currentTime + 0.25);
        } catch { /* AudioContext unavailable, ignore */ }
      }
      previousUnreadRef.current = newCount;
      setUnread(newCount);
    } catch (err) {
      // silently ignore — user might not be logged in
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/notifications/history?limit=${MAX_PREVIEW}`, { withCredentials: true });
      setItems(res.data.notifications || []);
    } catch (err) {
      console.error("notifications/history failed", err);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user?.user_id, user?.active_role]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      await fetchUnread();
      await fetchItems();
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Could not mark as read");
    }
  };

  const removeOne = async (n) => {
    const id = n.notification_id || n.id || n._id_str;
    if (!id) return;
    try {
      await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
      await fetchUnread();
      await fetchItems();
    } catch {
      toast.error("Could not delete");
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell-btn"
          aria-label="Notifications"
        >
          {unread > 0 ? (
            <BellRing className="w-5 h-5 text-[#0044CC]" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {unread > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] rounded-full"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] sm:w-[400px] max-h-[480px] overflow-hidden p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          <button
            onClick={markAllRead}
            className="text-xs text-[#0044CC] hover:underline disabled:opacity-50"
            disabled={unread === 0}
            data-testid="notification-mark-all-read"
          >
            <Check className="inline w-3 h-3 mr-0.5" /> Mark all as read
          </button>
        </div>
        <div className="overflow-y-auto max-h-[360px] divide-y divide-gray-100">
          {items.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-400 text-sm" data-testid="notification-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No notifications yet
            </div>
          ) : (
            items.map((n, idx) => {
              const isUnread = !n.read;
              return (
                <div
                  key={n.id || n._id_str || idx}
                  className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer flex gap-3 ${
                    isUnread ? "bg-blue-50/60" : ""
                  }`}
                  data-testid={`notification-item-${idx}`}
                  onClick={() => {
                    if (n.link) navigate(n.link);
                    setOpen(false);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {n.title || "Notification"}
                    </div>
                    <div className="text-xs text-gray-600 line-clamp-2">{n.body || ""}</div>
                    <div className="text-[11px] text-gray-400 mt-1">{formatRelative(n.sent_at)}</div>
                  </div>
                  {isUnread && <div className="w-2 h-2 rounded-full bg-[#0044CC] mt-1.5 flex-shrink-0" />}
                  <button
                    className="text-gray-300 hover:text-red-500 transition-colors"
                    onClick={(e) => { e.stopPropagation(); removeOne(n); }}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div className="border-t px-4 py-2.5 text-center">
          <button
            onClick={() => { setOpen(false); navigate("/dashboard/notifications"); }}
            className="text-sm text-[#0044CC] hover:underline font-medium"
            data-testid="notification-see-all"
          >
            See all notifications
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
